import React, { useState, useEffect } from 'react';
import { useSidebar } from './SidebarContext';
import Userfront from '../auth/userfrontCompat';
import api from './../api/axios';
import { Link } from 'react-router-dom';
import {
  FaHome, FaChartBar, FaEnvelope, FaBriefcase, FaCog, FaRobot, FaHeartbeat,
  FaUsers, FaCreditCard, FaAngleDoubleLeft, FaAngleDoubleRight
} from 'react-icons/fa';

import { useTranslation } from 'react-i18next';

const Sidebar = () => {
  const { collapsed, toggleSidebar } = useSidebar();
  const { t, i18n } = useTranslation();
  const [user, setUser] = useState(null);
  const [v2Enabled, setV2Enabled] = useState(false);
  const [hasPaidTeamAccess, setHasPaidTeamAccess] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userEmail = Userfront.user.email;
        if (!userEmail) return;
        const [userResponse, featureResponse, accessResponse] = await Promise.all([
          api.get('/user/user'),
          api.get('/operations/features').catch(() => ({ data: { leadgenflowV2: { enabled: false } } })),
          api.get('/subscription/access-status').catch(() => ({ data: { hasAccess: false, trialActive: false } })),
        ]);
        const currentUser = userResponse.data.user;
        setUser(currentUser);
        setV2Enabled(featureResponse.data?.leadgenflowV2?.enabled === true);
        setHasPaidTeamAccess(
          currentUser?.role === 'Admin' &&
          currentUser?.subscriptionType === 'Team' &&
          accessResponse.data?.hasAccess === true &&
          accessResponse.data?.trialActive !== true
        );
        if (currentUser.language) {
          i18n.changeLanguage(currentUser.language);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [i18n]);

  const getActiveClass = (path) =>
    window.location.pathname === path
      ? 'bg-blue-100 text-blue-700 font-semibold'
      : 'text-gray-600 hover:text-blue-600';

  const calculateRemainingDays = () => {
    if (user?.subscriptionType === 'trial' && user?.trialPeriodEnd) {
      const end = new Date(user.trialPeriodEnd);
      const now = new Date();
      return Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    }
    return 0;
  };

  if (loading) return null;

  const NavItem = ({ to, icon: Icon, label }) => (
    <Link
      to={to}
      className={`flex items-center h-12 px-3 rounded transition-all duration-200 ${getActiveClass(to)}`}
    >
      <div className={`flex items-center w-full ${collapsed ? 'justify-center' : 'gap-3'}`}>
        <Icon />
        {!collapsed && <span>{label}</span>}
      </div>
    </Link>
  );

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-30 bg-white border-r shadow-sm
        transition-all duration-300 ease-in-out
        ${collapsed ? 'w-16' : 'w-64'}
        hidden lg:flex flex-col`}
    >
      <div className="flex items-center justify-between px-4 py-4 border-b">
        <Link to="/" className="flex items-center gap-2">
          <img src="/logo.png" alt="Logo" className="h-8 w-8" />
          {!collapsed && <span className="text-lg font-bold text-gray-800">Leadgenflow</span>}
        </Link>
        <button onClick={toggleSidebar} className="text-gray-500 hover:text-blue-600">
          {collapsed ? <FaAngleDoubleRight /> : <FaAngleDoubleLeft />}
        </button>
      </div>

      <nav className={`flex-1 px-2 py-4 ${collapsed ? 'space-y-3' : 'space-y-2'}`}>
        <NavItem to="/" icon={FaHome} label={t('sidebar.dashboard')} />
        <NavItem to="/campaigns" icon={FaChartBar} label={t('sidebar.campaigns')} />
        {v2Enabled && <NavItem to="/agent/approvals" icon={FaRobot} label="AI approvals" />}
        {v2Enabled && <NavItem to="/operations" icon={FaHeartbeat} label="Operations" />}
        <NavItem to="/messages" icon={FaEnvelope} label={t('sidebar.inbox')} />
        <NavItem to="/leads" icon={FaBriefcase} label={t('sidebar.leads')} />
        <NavItem to="/settings" icon={FaCog} label={t('sidebar.settings')} />
        {hasPaidTeamAccess && (
          <NavItem to="/team" icon={FaUsers} label={t('sidebar.team')} />
        )}
        {user?.role === 'Admin' && (
          <NavItem to="/subscription" icon={FaCreditCard} label={t('sidebar.billing')} />
        )}
      </nav>

      {user?.subscriptionType === 'trial' && calculateRemainingDays() > 0 && (
        <div className="px-4 py-4 text-sm text-center bg-blue-50 border-t mt-auto">
          <p className="text-blue-600">
            {calculateRemainingDays() === 1
              ? t('navbar.trialEndsIn', { count: 1 })
              : t('navbar.trialEndsIn_plural', { count: calculateRemainingDays() })}
          </p>
          <Link
            to="/subscription"
            className="mt-2 inline-block text-sm text-white bg-blue-600 hover:bg-blue-700 px-3 py-1 rounded"
          >
            {t('navbar.upgradeNow')}
          </Link>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
